// Pure render model for the lattice map: join the static dot layout (positions + edges)
// with the live curriculumState → state-coloured nodes + edge geometry + the **context
// panel** for the current selection — a node's songs, or the drills that unlock an edge's
// skill/chord. No DOM. The SVG view blits this.
import type { CatalogEntry } from "../songs/catalog";
import { isChordAtom } from "../songs/chord-atoms";
import type { CurriculumState } from "../songs/curriculum-state";
import { difficulty } from "../songs/difficulty";
import type { LayoutNode, MapLayout } from "../songs/map-layout";

type NodeState = "mastered" | "unlocked" | "locked";

export interface RenderNode {
  id: string;
  label: string;
  x: number;
  y: number;
  state: NodeState;
  focused: boolean;
  songCount: number;
  num?: number; // stable reading-order node number (absent on root)
}
export interface RenderEdge {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** dot's routed spline (cubic control points); absent → draw a straight line. */
  points?: [number, number][];
  /** Label anchor (the spline midpoint when routed, else the straight midpoint). */
  labelX: number;
  labelY: number;
  label: string;
  kind: string;
  from: string;
  to: string;
  adds: string[];
  focused: boolean;
}
export interface PanelRow {
  id: string;
  title: string;
  state: NodeState;
  note: string | null;
  focused: boolean;
}
/** The right-hand context panel: a node's songs, or an edge's unlocking drills. */
export interface MapPanel {
  kind: "node" | "edge";
  num?: number; // node only: its stable reading-order number
  heading: string;
  variant: string | null; // node only: the shared arrangement chip
  note: string | null; // node: a shared lock reason; edge: "drills that teach it"
  rows: PanelRow[];
  empty: string | null; // shown instead of rows (e.g. an edge with no drill yet)
}
/** What the panel reflects. Nodes are D-pad- and mouse-selectable; edges mouse-only. */
export type MapSelection =
  | { kind: "node"; nodeId: string; rowIndex: number }
  | { kind: "edge"; from: string; to: string; adds: string[]; label: string; rowIndex: number };

export interface MapRenderModel {
  nodes: RenderNode[];
  edges: RenderEdge[];
  panel: MapPanel | null;
  proficiency: number;
}

export function buildMapRenderModel(
  layout: MapLayout,
  catalog: readonly CatalogEntry[],
  curriculum: CurriculumState,
  masteredIds: ReadonlySet<string>,
  selection: MapSelection,
): MapRenderModel {
  const entryById = new Map(catalog.map((e) => [e.id, e]));
  const songById = new Map(curriculum.songs.map((s) => [s.entry.id, s]));
  const title = (id: string): string => entryById.get(id)?.title ?? id;
  // Apply songs carry an unlock gate; drills (the teaching primitives) are always
  // practisable, so they read as "unlocked" until mastered.
  const stateOf = (id: string): NodeState =>
    masteredIds.has(id)
      ? "mastered"
      : songById.has(id)
        ? songById.get(id)?.unlocked
          ? "unlocked"
          : "locked"
        : "unlocked";
  const nodeState = (node: LayoutNode): NodeState => {
    if (node.isRoot) return "unlocked"; // the Start source — always reachable, has no songs to gate
    if (node.songIds.length === 0) return "locked";
    if (node.songIds.every((s) => masteredIds.has(s))) return "mastered";
    // Not all mastered → never "mastered" (a single mastered arrangement must not green the
    // whole bundle). The node is reachable if any song is unlocked or mastered; only an
    // all-locked node reads as locked.
    return node.songIds.some((s) => stateOf(s) !== "locked") ? "unlocked" : "locked";
  };

  const selNode = selection.kind === "node" ? selection.nodeId : null;
  const nodes: RenderNode[] = layout.nodes.map((n) => ({
    id: n.id,
    label: n.label,
    x: n.x,
    y: n.y,
    state: nodeState(n),
    focused: n.id === selNode,
    songCount: n.songIds.length,
    num: n.num,
  }));
  const pos = new Map(layout.nodes.map((n) => [n.id, n]));
  const edges: RenderEdge[] = layout.edges.flatMap((e) => {
    const a = pos.get(e.from);
    const b = pos.get(e.to);
    if (!a || !b) return [];
    const mid = e.points?.[Math.floor(e.points.length / 2)];
    const focused = selection.kind === "edge" && selection.from === e.from && selection.to === e.to;
    return [
      {
        x1: a.x,
        y1: a.y,
        x2: b.x,
        y2: b.y,
        points: e.points,
        labelX: mid?.[0] ?? (a.x + b.x) / 2,
        labelY: mid?.[1] ?? (a.y + b.y) / 2,
        label: e.label,
        kind: e.kind,
        from: e.from,
        to: e.to,
        adds: e.adds,
        focused,
      },
    ];
  });

  const panel =
    selection.kind === "node"
      ? buildNodePanel(layout, selection, title, stateOf, songById, masteredIds)
      : buildEdgePanel(
          selection,
          layout.nodes.find((n) => n.id === selection.to),
          catalog,
          stateOf,
        );
  // A multi-item node heading counts them as drills or tunes (not one tune standing
  // in for many — the F8 wart) rather than the lattice's representative label.
  if (panel?.kind === "node" && panel.rows.length > 1) {
    // Drills have no `uses` (pure teaching primitives); apply tunes do. (`teaches` no longer
    // discriminates — every apply node now carries a derived teaches delta.)
    const drills = panel.rows.every((r) => (entryById.get(r.id)?.uses?.length ?? 0) === 0);
    panel.heading = `${panel.rows.length} ${drills ? "drills" : "tunes"}`;
  }

  return { nodes, edges, panel, proficiency: curriculum.proficiency };
}

// Split a "<Tune> — <variant>" title on the arranger's em-dash delimiter. Null → no suffix.
function splitVariant(title: string): { tune: string; variant: string } | null {
  const m = title.match(/^(.*?) — (.+)$/);
  return m?.[1] && m[2] ? { tune: m[1], variant: m[2] } : null;
}

type SongLookup = Map<string, CurriculumState["songs"][number]>;

// A node's songs share one requirement bundle, so two redundancies are lifted to the
// node: the shared "— <texture>" suffix (variant) and the shared lock reason (note).
function buildNodePanel(
  layout: MapLayout,
  sel: Extract<MapSelection, { kind: "node" }>,
  title: (id: string) => string,
  stateOf: (id: string) => NodeState,
  songById: SongLookup,
  mastered: ReadonlySet<string>,
): MapPanel | null {
  const node = layout.nodes.find((n) => n.id === sel.nodeId);
  if (!node) return null;
  const lockNote = (id: string): string | null => {
    const s = songById.get(id);
    if (!s || s.unlocked || mastered.has(id)) return null;
    const lr = s.lockReason;
    return lr?.kind === "atom"
      ? `needs: ${[...lr.missing].sort().join(", ")}`
      : "above your level yet";
  };
  const titles = node.songIds.map(title);
  const parts = titles.map(splitVariant);
  const shared = parts[0]?.variant;
  const variant = shared && parts.every((p) => p?.variant === shared) ? shared : null;
  const locks = node.songIds.map(lockNote);
  const present = locks.filter((x): x is string => x !== null);
  const sharedLock =
    present.length > 0 && present.every((t) => t === present[0]) ? (present[0] ?? null) : null;
  const rows: PanelRow[] = node.songIds.map((id, i) => ({
    id,
    title: variant ? (parts[i]?.tune ?? titles[i] ?? id) : (titles[i] ?? id),
    state: stateOf(id),
    note: sharedLock ? null : (locks[i] ?? null),
    focused: i === sel.rowIndex,
  }));
  return {
    kind: "node",
    num: node.num,
    heading: node.label,
    variant,
    note: sharedLock,
    rows,
    empty: rows.length ? null : "No songs here yet",
  };
}

// The drills that teach the skill/chord an edge adds — "practise these to unlock it".
// A technique skill is taught key-agnostically (broken/alberti come in C/F/G; the triad
// drills carry their chord), but only the one in the *target song's key* is the drill to
// practise for *this* path — so narrow the suggestion to it. The gate stays keyless (any
// key still unlocks the skill); this is presentation only. Falls back to the full set when
// nothing matches (e.g. the target node spans no single key), so the panel never empties.
function buildEdgePanel(
  sel: Extract<MapSelection, { kind: "edge" }>,
  target: LayoutNode | undefined,
  catalog: readonly CatalogEntry[],
  stateOf: (id: string) => NodeState,
): MapPanel {
  // Only true drills belong here: apply pieces (uses-non-empty) carry a `teaches` too — the
  // chords they introduce — so without this they'd masquerade as the edge's unlock drills.
  const isDrill = (e: CatalogEntry): boolean => (e.uses?.length ?? 0) === 0;
  // A drill is *directly related* to this edge iff every skill it teaches is among the skills
  // held once you've taken the edge — the target node's requirements (which include the edge's
  // own `adds`). This drops drills that drag in an unrelated skill the target never uses — e.g.
  // a contrary-motion drill that also teaches hand-independence on a bare +five-finger edge.
  const held = new Set([...sel.adds, ...(target?.requirements ?? []), ...(target?.chords ?? [])]);
  const candidates = catalog
    .filter(
      (e) =>
        isDrill(e) &&
        (e.teaches ?? []).some((t) => sel.adds.includes(t)) &&
        (e.teaches ?? []).every((t) => held.has(t)),
    )
    .sort((a, b) => difficulty(a) - difficulty(b) || a.id.localeCompare(b.id));
  const keyOf = new Map(catalog.map((e) => [e.id, e.key]));
  const nodeKeys = new Set((target?.songIds ?? []).map((id) => keyOf.get(id)).filter(Boolean));
  const nodeChords = new Set(target?.chords ?? []);
  // In-key for this song: a drill that teaches chord(s) is relevant iff every chord it
  // teaches is one the target song uses (the triad/chord drills — all notated in C, so
  // notated key can't discriminate them); a pure-technique drill (broken/alberti, no
  // chord) is relevant iff its notated key is the song's.
  const inKey = (e: CatalogEntry): boolean => {
    const taught = e.teaches ?? [];
    if (taught.includes("five-finger")) return true; // universal prerequisite, key-agnostic
    const chordTaught = taught.filter(isChordAtom);
    return chordTaught.length > 0
      ? chordTaught.every((c) => nodeChords.has(c))
      : !!e.key && nodeKeys.has(e.key);
  };
  const matched = candidates.filter(inKey);
  const drills = matched.length > 0 ? matched : candidates;
  const rows: PanelRow[] = drills.map((e, i) => ({
    id: e.id,
    title: e.title,
    state: stateOf(e.id),
    note: null,
    focused: i === sel.rowIndex,
  }));
  return {
    kind: "edge",
    heading: `Unlock ${sel.label.replace(/^\+/, "")}`,
    variant: null,
    note: rows.length ? "drills that teach it" : null,
    rows,
    empty: rows.length ? null : "No drill for this yet — coming soon",
  };
}
